(() => {
  const productInfo = document.querySelector('product-info[data-keast-storefront-class]');
  if (!productInfo) return;

  let lastQuestionTrigger = null;
  let syncQueued = false;

  const TEST_VERSION = 'catalog-intent-v1-14d';
  const inMemoryPublished = new Set();
  const PRIVACY_FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
  let privacyLoadAttempts = 0;

  const ensurePrivacyApi = () => {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') return;
    const loader = window.Shopify?.loadFeatures;
    if (typeof loader === 'function') {
      loader(PRIVACY_FEATURE, () => {});
      return;
    }
    privacyLoadAttempts += 1;
    if (privacyLoadAttempts < 20) window.setTimeout(ensurePrivacyApi, 250);
  };

  ensurePrivacyApi();

  const privacyAllowsAnalytics = () =>
    window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;

  const currentContext = () => {
    const purchaseMeta = productInfo.querySelector('[data-keast-purchase-meta]');
    return {
      product_id: String(productInfo.dataset.productId || purchaseMeta?.dataset.productId || ''),
      variant_id: String(purchaseMeta?.dataset.variantId || ''),
      storefront_class: String(productInfo.dataset.keastStorefrontClass || purchaseMeta?.dataset.storefrontClass || ''),
      page_path: window.location.pathname,
      test_version: TEST_VERSION,
    };
  };

  const hasPublished = (key) => {
    if (inMemoryPublished.has(key)) return true;
    try {
      return window.sessionStorage.getItem(`keast-intent:${key}`) === '1';
    } catch (_error) {
      return false;
    }
  };

  const markPublished = (key) => {
    inMemoryPublished.add(key);
    try {
      window.sessionStorage.setItem(`keast-intent:${key}`, '1');
    } catch (_error) {
      // Session storage can be unavailable. The in-memory guard still prevents
      // duplicate events for the current page view.
    }
  };

  const publishIntent = (eventName, details = {}, dedupeSuffix = eventName) => {
    if (!privacyAllowsAnalytics()) return false;
    const context = { ...currentContext(), ...details };
    const dedupeKey = `${TEST_VERSION}:${context.page_path}:${dedupeSuffix}`;
    if (hasPublished(dedupeKey)) return false;

    const publisher = window.Shopify?.analytics?.publish;
    if (typeof publisher !== 'function') return false;
    publisher(eventName, context);

    markPublished(dedupeKey);
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...context });
    }
    return true;
  };

  const questionDialog = productInfo.querySelector('[data-keast-question-dialog]');
  const stickyPurchase = productInfo.querySelector('[data-keast-sticky-purchase]');
  const financingModule = productInfo.querySelector('[data-keast-financing]');
  const financingDetailsToggle = financingModule?.querySelector('[data-keast-financing-details-toggle]');
  const financingDetailsPanel = financingModule?.querySelector('[data-keast-financing-details-panel]');
  if (questionDialog) document.body.append(questionDialog);
  if (stickyPurchase) document.body.append(stickyPurchase);

  // Equipment form qualification v1: a conservative reporting signal.
  // Reads the question only in memory. It never stores or transmits the message.
  function classifyEquipmentInquiry({ message, pagePath, productTitle, variantTitle, storefrontClass }) {
    const version = 'equipment-form-quality-v1';
    const result = (status, reason) => ({ version, status, reason, method: 'rules-v1' });
    if (storefrontClass !== 'equipment' || !/^\/products\/[a-z0-9][a-z0-9-]*\/?$/.test(String(pagePath || ''))) {
      return result('out_of_scope', 'not_equipment_product');
    }
    if (/^rental-/i.test(String(pagePath).split('/products/')[1] || '') || /^rental\s*[-–—:]/i.test(String(productTitle || '').trim())) {
      return result('rental_inquiry', 'rental_product');
    }
    if (/\b(?:rental|renting)\b/i.test(String(variantTitle || ''))) return result('rental_inquiry', 'rental_product');
    const raw = String(message || '');
    if (raw.length > 6000) return result('uncertain', 'message_too_long');
    const text = raw.normalize('NFKC').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (text.length < 5) return result('uncertain', 'insufficient_detail');
    if (/^(?:test\s*[-—–:]\s*ignore|owner[- ]approved tracking test)|\bkeast[-_ ](?:meta[-_ ])?qa[-_ ]\d/.test(text)) {
      return result('test', 'labeled_test');
    }
    if (/ignore (?:all |previous |the )?(?:instructions|rules)|(?:mark|classify|label) (?:this|me|it|the lead) (?:as )?(?:qualified|high[- ]intent)|system prompt|<\|(?:system|assistant)/.test(text)) {
      return result('uncertain', 'classification_directive');
    }
    if (/\b(?:backlinks?|guest post|domain authority|seo services|search engine optimization|website redesign|digital marketing services|lead generation services|increase your (?:website )?traffic)\b/.test(text)) {
      return result('vendor_or_spam', 'promotional_message');
    }
    if (/\b(?:looking for (?:a )?job|job application|employment opportunity|resume attached)\b/.test(text)) {
      return result('other_inquiry', 'employment');
    }
    const explicitMachinePurchase = /\b(?:buy|purchase|order|purchasing)\b.{0,70}\b(?:this|one|unit|machine|equipment|mulcher|grain cart|feed wagon|grinder|spade|mower|trailer|tiller)\b/.test(text);
    const parts = /\b(?:part (?:number|no\.?|#)|replacement (?:parts?|belt|bearing|blade|motor|hose|seal|filter|chain|teeth)|spare (?:parts?|teeth)|parts? (?:for|diagram|manual)|need (?:a |the |some )?(?:belt|bearing|seal|filter))\b/.test(text);
    const rental = /\b(?:rental|renting|for rent|rent (?:one|this|it|a|the)|daily rate|weekly rate|rent[- ]to[- ]own)\b/.test(text);
    const service = /\b(?:cancel (?:my|our|the|an?) order|cancel my purchase|order status|track (?:my|our|the) order|where is my order|refund (?:my|our)|request (?:a )?refund|want (?:a )?refund|return (?:my|our|the) (?:order|unit|machine)|repair (?:my|our|a|the)|fix (?:my|our)|troubleshoot|not working|warranty claim|service (?:my|our)|operator(?:'s)? manual|owners? manual)\b/.test(text);
    const supplier = /\b(?:become (?:a |your )?(?:supplier|vendor)|supply (?:you|your company)|sell (?:you|your company)|distributor partnership|wholesale partnership)\b/.test(text);
    const nonBuyingKinds = [parts, rental, service, supplier].filter(Boolean).length;
    if (nonBuyingKinds > 1 || (nonBuyingKinds && explicitMachinePurchase)) return result('uncertain', 'mixed_purpose');
    if (parts) return result('parts_inquiry', 'parts_request');
    if (rental) return result('rental_inquiry', 'rental_request');
    if (service) return result('service_inquiry', 'service_request');
    if (supplier) return result('vendor_or_spam', 'supplier_request');
    if (/\b(?:not interested in (?:buying|purchasing)|do not want to buy|don'?t want to buy)\b/.test(text)) return result('uncertain', 'purchase_negated');
    if (/\b(?:buy|buying|purchase|purchasing|order|ordering|interested|looking to buy|ready to buy|want one|need one)\b/.test(text)) return result('buying_inquiry', 'purchase_interest');
    if (/\b(?:available|availability|in stock|stock status|lead time|delivery time|how soon)\b/.test(text)) return result('buying_inquiry', 'availability_question');
    if (/\b(?:price|pricing|cost|quote|how much|best deal|discount)\b/.test(text)) return result('buying_inquiry', 'price_question');
    if (/\b(?:deliver(?:ed|y)?|shipping|freight|pickup|pick up)\b/.test(text)) return result('buying_inquiry', 'delivery_question');
    if (/\b(?:financ(?:e|ing)|monthly payment|annual payment|down payment|credit application|lease purchase)\b/.test(text)) return result('buying_inquiry', 'finance_question');
    if (/\b(?:compatible|compatibility)\b|\b(?:will|would|does|can) (?:this|it|that|the [a-z0-9-]+) (?:fit|work|run|handle)\b|\bwhat (?:size|model).{0,70}(?:need|recommend)\b/.test(text)) return result('buying_inquiry', 'application_question');
    return result('uncertain', 'insufficient_buying_context');
  }

  questionDialog?.querySelector('.keast-question-form')?.addEventListener('submit', (event) => {
    if (currentContext().storefront_class !== 'equipment' || !privacyAllowsAnalytics()) return;
    try {
      const pending = { ...currentContext(), submitted_at: Date.now() };
      try {
        const form = event.currentTarget;
        const quality = classifyEquipmentInquiry({
          message: form.elements.namedItem('contact[Question]')?.value || '',
          productTitle: form.elements.namedItem('contact[Product]')?.value || '',
          variantTitle: form.elements.namedItem('contact[Variant]')?.value || '',
          pagePath: pending.page_path,
          storefrontClass: pending.storefront_class,
        });
        const id = typeof window.crypto?.randomUUID === 'function' ? window.crypto.randomUUID() : '';
        if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(id)) {
          const flags = new URLSearchParams(window.location.search);
          pending.qualification = {
            ...quality,
            id: `qf-${id.toLowerCase()}`,
            is_test: ['gtm_debug', 'gtm_preview', 'preview_theme_id', 'preview_token', 'codex_test',
              'keast_qa', 'qa_test', 'keast_tracking_test', 'test_event_code'].some(key => flags.has(key)) || quality.status === 'test',
          };
        }
      } catch (_qualificationError) {
        // Qualification failure cannot interrupt a form or its existing events.
      }
      window.sessionStorage.setItem('keast-intent:pending-specialist-form', JSON.stringify(pending));
    } catch (_error) {
      // The success bridge fails closed when session storage is unavailable.
    }
  });

  const getMainSubmit = () => productInfo.querySelector('.product-form__submit');
  const getSticky = () => stickyPurchase;
  const getOpenDialog = () => (questionDialog && !questionDialog.hidden ? questionDialog : null);

  const formatFinancingMoney = (amountCents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amountCents / 100);
    } catch (_error) {
      return `$${Math.round(amountCents / 100).toLocaleString('en-US')}`;
    }
  };

  const calculateAnnualPayment = (principalCents, annualInterestRate, annualPayments) => {
    if (principalCents <= 0 || annualPayments <= 0) return null;
    const annualRate = annualInterestRate / 100;
    if (annualRate === 0) return principalCents / annualPayments;
    const growth = (1 + annualRate) ** annualPayments;
    return principalCents * ((annualRate * growth) / (growth - 1));
  };

  const setFinancingText = (selector, value) => {
    financingModule?.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  };

  const setFinancingDetailsOpen = (open) => {
    if (!financingDetailsToggle || !financingDetailsPanel) return;
    financingDetailsToggle.setAttribute('aria-expanded', String(open));
    financingDetailsPanel.hidden = !open;
  };

  financingDetailsToggle?.addEventListener('click', () => {
    const willOpen = financingDetailsToggle.getAttribute('aria-expanded') !== 'true';
    setFinancingDetailsOpen(willOpen);
    if (willOpen) {
      publishIntent('keast_product_interaction', { interaction_type: 'financing_details' }, 'interaction:financing_details');
    }
  });

  const syncFinancing = (variant) => {
    if (!financingModule) return;

    const rawPriceCents = String(variant?.price ?? financingModule.dataset.financingVariantPriceCents ?? '').trim();
    const available = variant?.available ?? financingModule.dataset.financingVariantAvailable === 'true';
    if (variant) {
      financingModule.dataset.financingVariantPriceCents = String(variant.price);
      financingModule.dataset.financingVariantAvailable = String(variant.available);
    }

    const isEquipment = financingModule.dataset.financingStorefrontClass === 'equipment';
    const estimatesEnabled = financingModule.dataset.financingEnabled === 'true';
    const rawMinimumCents = String(financingModule.dataset.financingMinimumCents ?? '').trim();
    const rawDisplayMinimumCents = String(financingModule.dataset.financingDisplayMinimumCents ?? '').trim();
    const rawFixedRate = String(financingModule.dataset.financingFixedRate ?? '').trim();
    const rawAnnualPayments = String(financingModule.dataset.financingAnnualPayments ?? '').trim();
    const rawDownPaymentPercent = String(financingModule.dataset.financingDownPaymentPercent ?? '').trim();
    const priceCents = Number(rawPriceCents);
    const minimumCents = Number(rawMinimumCents);
    const displayMinimumCents = Number(rawDisplayMinimumCents);
    const fixedRate = Number(rawFixedRate);
    const annualPayments = Number(rawAnnualPayments);
    const downPaymentPercent = Number(rawDownPaymentPercent);
    const currency = financingModule.dataset.financingCurrency || 'USD';
    const estimateWrap = financingModule.querySelector('[data-keast-financing-estimate-wrap]');
    const rentalOption = financingModule.querySelector('.keast-rental-option');
    const fallback = financingModule.querySelector('[data-keast-financing-fallback]');
    const syncModuleVisibility = () => {
      financingModule.hidden = Boolean(estimateWrap?.hidden && fallback?.hidden !== false && !rentalOption);
    };

    const inputsAreComplete =
      rawPriceCents !== '' &&
      rawMinimumCents !== '' &&
      rawDisplayMinimumCents !== '' &&
      rawFixedRate !== '' &&
      rawAnnualPayments !== '' &&
      rawDownPaymentPercent !== '';
    const inputsAreValid =
      inputsAreComplete &&
      Number.isFinite(priceCents) &&
      Number.isSafeInteger(priceCents) &&
      Number.isFinite(minimumCents) &&
      minimumCents > 0 &&
      Number.isFinite(displayMinimumCents) &&
      displayMinimumCents >= 0 &&
      Number.isFinite(fixedRate) &&
      fixedRate >= 0 &&
      Number.isFinite(annualPayments) &&
      Number.isInteger(annualPayments) &&
      annualPayments > 0 &&
      Number.isFinite(downPaymentPercent) &&
      downPaymentPercent >= 0 &&
      downPaymentPercent < 100;
    const financedPrincipalCents = priceCents * (1 - downPaymentPercent / 100);
    const showFinancing = estimatesEnabled && isEquipment && available && rawDisplayMinimumCents !== '' &&
      Number.isFinite(displayMinimumCents) && Number.isFinite(priceCents) && priceCents > displayMinimumCents;
    if (fallback) fallback.hidden = !showFinancing;
    const eligible =
      showFinancing &&
      inputsAreValid &&
      currency === 'USD' &&
      financedPrincipalCents >= minimumCents;

    if (!eligible) {
      if (estimateWrap) estimateWrap.hidden = true;
      setFinancingDetailsOpen(false);
      syncModuleVisibility();
      return;
    }

    const annualInterestRate = fixedRate;
    const rawPaymentCents = calculateAnnualPayment(financedPrincipalCents, annualInterestRate, annualPayments);
    if (!Number.isFinite(rawPaymentCents)) {
      if (estimateWrap) estimateWrap.hidden = true;
      syncModuleVisibility();
      return;
    }

    const annualPaymentCents = Math.ceil(rawPaymentCents / 100) * 100;
    const totalPaymentsCents = annualPaymentCents * annualPayments;
    const formattedPrice = formatFinancingMoney(priceCents, currency);
    const formattedPayment = formatFinancingMoney(annualPaymentCents, currency);
    const formattedTotal = formatFinancingMoney(totalPaymentsCents, currency);

    setFinancingText('[data-keast-financing-payment]', formattedPayment);
    setFinancingText('[data-keast-financing-disclaimer-price]', formattedPrice);
    setFinancingText('[data-keast-financing-disclaimer-rate]', String(annualInterestRate));
    setFinancingText('[data-keast-financing-disclaimer-payment]', formattedPayment);
    setFinancingText('[data-keast-financing-disclaimer-total]', formattedTotal);
    if (estimateWrap) estimateWrap.hidden = false;
    if (fallback) fallback.hidden = true;
    syncModuleVisibility();
  };

  const syncContactVariant = () => {
    const variantInput = questionDialog?.querySelector('[data-keast-form-variant]');
    const variantTitle = productInfo.querySelector('[data-keast-purchase-meta]')?.dataset.variantTitle;
    if (variantInput && variantTitle !== undefined) variantInput.value = variantTitle;
  };

  const syncSticky = () => {
    syncQueued = false;
    const mainSubmit = getMainSubmit();
    const sticky = getSticky();
    if (!mainSubmit || !sticky) return;

    const stickySubmit = sticky.querySelector('[data-keast-sticky-submit]');
    if (!stickySubmit) return;

    const mainLabel = mainSubmit.querySelector('span')?.textContent?.trim() || mainSubmit.textContent.trim();
    const shouldDisable = mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true';
    if (stickySubmit.textContent.trim() !== mainLabel) stickySubmit.textContent = mainLabel;
    if (stickySubmit.disabled !== shouldDisable) stickySubmit.disabled = shouldDisable;

    const mainPurchasePassed = mainSubmit.getBoundingClientRect().bottom <= 0;
    const shouldShow = mainPurchasePassed && !getOpenDialog();
    sticky.classList.toggle('is-visible', shouldShow);
    sticky.setAttribute('aria-hidden', String(!shouldShow));
    document.body.classList.toggle('keast-sticky-active', shouldShow);
    syncContactVariant();
  };

  const queueStickySync = () => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(syncSticky);
  };

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data?.variant || data.sectionId !== productInfo.dataset.section) return;

    const purchaseMeta = productInfo.querySelector('[data-keast-purchase-meta]');
    if (purchaseMeta) {
      purchaseMeta.dataset.variantId = String(data.variant.id);
      purchaseMeta.dataset.variantTitle = data.variant.title;
    }

    const variantInput = questionDialog?.querySelector('[data-keast-form-variant]');
    if (variantInput) variantInput.value = data.variant.title;
    syncFinancing(data.variant);
    publishIntent('keast_product_interaction', { interaction_type: 'variant_change' }, `interaction:variant:${data.variant.id}`);
    queueStickySync();
  });

  const observedPartsForms = new WeakSet();
  const repairPartsFormLabels = (attempt = 0) => {
    if (currentContext().storefront_class !== 'parts') return;
    const root = questionDialog?.querySelector('shopify-forms-embed')?.shadowRoot;
    if (!root) {
      if (attempt < 50 && getOpenDialog()) window.setTimeout(() => repairPartsFormLabels(attempt + 1), 200);
      return;
    }
    const connectLabels = () => {
      root.querySelectorAll('textarea[name]').forEach((field) => {
        if (field.id || field.hasAttribute('aria-label') || field.hasAttribute('aria-labelledby')) return;
        const label = field.parentElement?.querySelector('label[for]');
        // Shopify Forms supplies this visible label, but can omit the textarea ID.
        if (label?.htmlFor === field.name && !root.getElementById(label.htmlFor)) field.id = label.htmlFor;
      });
    };
    connectLabels();
    if (observedPartsForms.has(root)) return;
    observedPartsForms.add(root);
    new MutationObserver(connectLabels).observe(root, { childList: true, subtree: true });
  };

  const openQuestionDialog = (trigger) => {
    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : questionDialog;
    if (!dialog) return;

    lastQuestionTrigger = trigger;
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('keast-dialog-open');
    repairPartsFormLabels();
    if (currentContext().storefront_class === 'equipment') {
      publishIntent(
        'keast_specialist_form_open',
        { action_location: trigger.dataset.keastActionLocation || 'inline' },
        'specialist_form_open'
      );
    }
    queueStickySync();

    requestAnimationFrame(() => {
      const firstFocus = dialog.querySelector(
        '.keast-question-form input:not([type="hidden"]), .keast-question-form textarea, .keast-question-form select'
      ) || dialog.querySelector('.keast-question-dialog__close');
      firstFocus?.focus();
    });
  };

  const closeQuestionDialog = (dialog) => {
    if (!dialog) return;
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('keast-dialog-open');
    queueStickySync();
    lastQuestionTrigger?.focus();
  };

  const trapDialogFocus = (event, dialog) => {
    if (event.key !== 'Tab') return;
    const focusable = [];
    const collectFocusable = (root) => {
      for (const element of root.querySelectorAll('*')) {
        if (element.matches('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') &&
            !element.hidden && element.getClientRects().length && element.tabIndex >= 0) {
          focusable.push(element);
        }
        if (element.shadowRoot) collectFocusable(element.shadowRoot);
      }
    };
    collectFocusable(dialog);
    if (!focusable.length) return;

    let activeElement = document.activeElement;
    while (activeElement?.shadowRoot?.activeElement) activeElement = activeElement.shadowRoot.activeElement;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const callTrigger = event.target.closest('[data-keast-call]');
    if (callTrigger) {
      publishIntent(
        'keast_click_to_call',
        { action_location: callTrigger.dataset.keastActionLocation || 'inline' },
        `call:${callTrigger.dataset.keastActionLocation || 'inline'}`
      );
    }

    const openTrigger = event.target.closest('[data-keast-question-open]');
    if (openTrigger && productInfo.contains(openTrigger)) {
      openQuestionDialog(openTrigger);
      return;
    }

    const closeTrigger = event.target.closest('[data-keast-question-close]');
    if (closeTrigger) {
      closeQuestionDialog(closeTrigger.closest('[data-keast-question-dialog]'));
      return;
    }

    const stickySubmit = event.target.closest('[data-keast-sticky-submit]');
    if (stickySubmit && stickyPurchase?.contains(stickySubmit)) {
      const mainSubmit = getMainSubmit();
      if (mainSubmit && !mainSubmit.disabled && mainSubmit.getAttribute('aria-disabled') !== 'true') {
        mainSubmit.click();
      }
      return;
    }

    const interactionTarget = event.target.closest(
      '.product__media-list, .thumbnail-list, .product__accordion > details > summary, .pickup-availability-button'
    );
    if (interactionTarget && productInfo.contains(interactionTarget)) {
      let interactionType = 'gallery';
      if (interactionTarget.matches('.product__accordion > details > summary')) interactionType = 'description';
      if (interactionTarget.matches('.pickup-availability-button')) interactionType = 'pickup_check';
      publishIntent(
        'keast_product_interaction',
        { interaction_type: interactionType },
        `interaction:${interactionType}`
      );
    }
  });

  document.addEventListener('keydown', (event) => {
    const dialog = getOpenDialog();
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeQuestionDialog(dialog);
      return;
    }
    trapDialogFocus(event, dialog);
  });

  const mutationObserver = new MutationObserver(queueStickySync);
  mutationObserver.observe(productInfo, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled'],
  });

  window.addEventListener('resize', queueStickySync, { passive: true });
  window.addEventListener('scroll', queueStickySync, { passive: true });

  let activeSeconds = 0;
  const engagementTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
    activeSeconds += 1;
    if (activeSeconds >= 30) {
      const published = publishIntent('keast_product_engaged', { active_seconds: 30 }, 'engaged:30');
      if (published) window.clearInterval(engagementTimer);
    }
  }, 1000);

  const pendingScrollDepths = new Set([50, 90].filter((depth) =>
    !hasPublished(`${TEST_VERSION}:${window.location.pathname}:scroll:${depth}`)
  ));
  let scrollDepthFrame = 0;
  const trackScrollDepth = () => {
    scrollDepthFrame = 0;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const depth = Math.round((window.scrollY / scrollable) * 100);
    for (const milestone of pendingScrollDepths) {
      if (depth < milestone) continue;
      const suffix = `scroll:${milestone}`;
      if (publishIntent('keast_product_scroll', { depth_percent: milestone }, suffix) ||
          hasPublished(`${TEST_VERSION}:${window.location.pathname}:${suffix}`)) {
        pendingScrollDepths.delete(milestone);
      }
    }
    if (!pendingScrollDepths.size) window.removeEventListener('scroll', queueScrollDepth);
  };
  const queueScrollDepth = () => {
    if (!scrollDepthFrame) scrollDepthFrame = requestAnimationFrame(trackScrollDepth);
  };
  if (pendingScrollDepths.size) window.addEventListener('scroll', queueScrollDepth, { passive: true });

  syncFinancing();

  const successfulForm = questionDialog?.querySelector('[data-keast-form-success]');
  if (successfulForm && currentContext().storefront_class === 'equipment') {
    publishIntent('keast_specialist_form_success', {}, 'specialist_form_success');
    const successfulDialog = successfulForm.closest('[data-keast-question-dialog]');
    if (successfulDialog) {
      successfulDialog.hidden = false;
      successfulDialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('keast-dialog-open');
    }
  }

  queueStickySync();
})();
